import { IkeUnit } from '@ikegps/core-shared';
import type { CanvasElement, Content } from 'pdfmake/interfaces';
import type { IkeUnitDistance } from '@ikegps/core-api';

// The bar grows up to this width (PDF points); the round distance is the largest "nice" value that fits.
const MAX_BAR_POINTS = 130;
const PAD = 6;
const TICK_HEIGHT = 5;
const FONT_SIZE = 8;
const LABEL_HEIGHT = 10;
const FEET_PER_MILE = 5280;
const METERS_PER_FOOT = 0.3048;
const METERS_PER_MILE = FEET_PER_MILE * METERS_PER_FOOT;

interface Anchor {
	x: number;
	y: number;
	width: number;
	height: number;
}

interface Bar {
	meters: number;
	label: string;
}

/**
 * A true-to-ground scale bar pinned to the bottom-left of the map image. `metersPerPoint` is how many
 * ground metres one PDF point of the placed image spans; from it we pick a round distance (1/2/5 ×10ⁿ)
 * in the user's unit and size the bar to match. Returns `[]` when the scale is unknown.
 */
export function scaleBar(map: Anchor, metersPerPoint: number, unit: IkeUnitDistance): Content[] {
	if (!(metersPerPoint > 0) || !Number.isFinite(metersPerPoint)) {
		return [];
	}

	const bar = niceBar(MAX_BAR_POINTS * metersPerPoint, unit);
	const barLength = bar.meters / metersPerPoint;
	// Size the plate to the wider of the bar or its label, so a long label on a short bar still fits.
	// `alignment` is unreliable under `absolutePosition`, so bar and label are centered by hand.
	const labelWidth = bar.label.length * FONT_SIZE * 0.55;
	const contentWidth = Math.max(barLength, labelWidth);

	const plateWidth = contentWidth + PAD * 2;
	const plateHeight = PAD * 2 + LABEL_HEIGHT + TICK_HEIGHT;
	const x = map.x + 10;
	const y = map.y + map.height - 10 - plateHeight;
	const baselineY = plateHeight - PAD;

	return [
		{
			absolutePosition: { x, y },
			canvas: [
				{ type: 'rect', x: 0, y: 0, w: plateWidth, h: plateHeight, r: 3, color: '#ffffff', fillOpacity: 0.8 },
				...barShapes(PAD + (contentWidth - barLength) / 2, baselineY, barLength),
			],
		},
		{
			text: bar.label,
			absolutePosition: { x: x + PAD + (contentWidth - labelWidth) / 2, y: y + PAD - 1 },
			fontSize: FONT_SIZE,
			bold: true,
			color: '#000000',
		},
	];
}

const BAR_LINE_WIDTH = 1.2;

/**
 * The bar: two end ticks joined by the baseline, drawn as one polyline so the right-angle corners
 * get a filled miter join (separate segments would leave a notch where their butt caps meet). The
 * midpoint tick is a separate line — its end sits inside the baseline stroke, so it needs no join.
 */
function barShapes(left: number, baselineY: number, length: number): CanvasElement[] {
	const right = left + length;
	const tickTop = baselineY - TICK_HEIGHT;
	return [
		{
			type: 'polyline',
			lineWidth: BAR_LINE_WIDTH,
			lineColor: '#000000',
			lineJoin: 'miter',
			points: [
				{ x: left, y: tickTop },
				{ x: left, y: baselineY },
				{ x: right, y: baselineY },
				{ x: right, y: tickTop },
			],
		},
		{
			type: 'line',
			x1: left + length / 2,
			y1: baselineY,
			x2: left + length / 2,
			y2: tickTop,
			lineWidth: BAR_LINE_WIDTH,
			lineColor: '#000000',
		},
	];
}

/** Pick the largest 1/2/5 ×10ⁿ distance not exceeding `maxMeters`, expressed in the user's unit. */
function niceBar(maxMeters: number, unit: IkeUnitDistance): Bar {
	if (unit === IkeUnit.METER) {
		if (maxMeters >= 1000) {
			const km = niceRound(maxMeters / 1000);
			return { meters: km * 1000, label: `${formatNumber(km)} km` };
		}
		const meters = niceRound(maxMeters);
		return { meters, label: `${formatNumber(meters)} m` };
	}

	const maxFeet = maxMeters / METERS_PER_FOOT;
	if (maxFeet >= FEET_PER_MILE) {
		const miles = niceRound(maxMeters / METERS_PER_MILE);
		return { meters: miles * METERS_PER_MILE, label: `${formatNumber(miles)} mi` };
	}
	const feet = niceRound(maxFeet);
	return { meters: feet * METERS_PER_FOOT, label: `${formatNumber(feet)} ft` };
}

function niceRound(value: number): number {
	if (!(value > 0)) {
		return 0;
	}
	const magnitude = 10 ** Math.floor(Math.log10(value));
	const fraction = value / magnitude;
	const niceFraction = fraction >= 5 ? 5 : fraction >= 2 ? 2 : 1;
	return niceFraction * magnitude;
}

/** Trim trailing zeros so e.g. 0.5 stays "0.5" but 100 isn't "100.0". */
function formatNumber(value: number): string {
	return Number(value.toFixed(2)).toString();
}
